import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'

type SendDataAsAttachmentOptions = {
  subject?: string
  text?: string
  filename?: string
}

export const useSendDataAsAttachment = () => {
  return useMutation({
    mutationFn: async ({
      data,
      options = {},
    }: {
      data: unknown[]
      options?: SendDataAsAttachmentOptions
    }) => {
      if (!data || data.length === 0) {
        throw new Error('No data to send')
      }

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(data)

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data')

      // Convert workbook to binary string
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' })

      // Convert binary string to base64
      const base64Data = btoa(
        String.fromCharCode.apply(
          null,
          Array.from(new Uint8Array(excelBuffer.split('').map((c: string) => c.charCodeAt(0))))
        )
      )

      // Generate filename with date if not provided
      const now = new Date()
      const isoDate = now.toISOString().split('T')[0] // YYYY-MM-DD format
      const defaultFilename = `data_${isoDate}.xlsx`
      const filename = options.filename || defaultFilename

      // Send email with the Excel file
      const response = await fetch('/api/send_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: options.subject || 'Data Export',
          text: options.text || 'Please find the attached data export.',
          attachments: [
            {
              filename,
              content: base64Data,
              contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
          ],
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Failed to send email')
      }

      return await response.json()
    },
    onSuccess: () => {
      toast.success('Email sent successfully')
    },
    onError: (error: Error) => {
      console.error('Error sending email:', error)
      toast.error(`Failed to send email: ${error.message}`)
    },
  })
}
